import React, { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Guid } from "guid-typescript";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// COMPONENTS
import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputAutoCompleteCheckControlled from "@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled";
import { AreaButton } from "@/main/components/shared/styles";

// REDUX/UTILS/HOOKS
import VehicleAutoCompleteComponent from "@/main/components/shared/vehicle/VehicleAutoCompleteComponent";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { KeyValue } from "@/main/models/common";
import {
	BodyShopFilterRequest,
	InsurerFilterRequest,
	ProcurementReportScheduleSaveRequest,
	ProcurementReportScheduleSearchGridRequest,
	SupplierFilterRequest,
} from "@/main/models/request";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByFullKey,
	getTranslateByKey,
	isNullUndefined,
	orderDataByValue,
	removeEmptyValues,
} from "@/main/services/utils";
import {
	formatDateToISOString,
	getAllKeysIgnoreValidationsDate,
	initalDateForm,
	onLoadDateRangePicker,
	TypeKeysValidationsDate,
	validateFormDate,
} from "@/main/services/utilsDate";
import { userIsBodyshop, userIsInsurer, userIsSupplier } from "@/main/services/utilsUser";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { procurementReportScheduleApi } from "@/main/api";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { StatusProcurementReportScheduleEnum } from "@/main/services/enum/StatusProcurementReportSchedule";
import {
	getReportScheduleStatus,
	resetReducerReportScheduleStatus,
	useReportScheduleStatusReducer,
} from "@/main/store/reportScheduleStatus";
import { getSchedulerUsers, useSchedulerUsersReducer } from "@/main/store/schedulerUsers";
import { getSuppliers, resetReducerSuppliers, useSupplierReducer } from "@/main/store/supplier";

import { IEditDataSchedule } from "../types";
import * as S from "./styles";

interface IFiltersOrderReportScheduleProps {
	showFilters: boolean;
	dataEdit: IEditDataSchedule;
	updateDataEdit: (data: IEditDataSchedule) => void;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	onResetSearch: () => void;
	getData: (filters: ProcurementReportScheduleSearchGridRequest) => void;
}

function FiltersOrderReportSchedule({
	showFilters,
	dataEdit,
	updateDataEdit,
	getData,
	onResetSearch,
	setShowFilters,
}: IFiltersOrderReportScheduleProps) {
	//#region Hooks
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const parameterRangeDays = useParameterByTypeScreenReducer(
		ParameterTypeEnum.DefaultSearchOrderReportScheduleDaysInterval,
	);
	const parameterRangeDaysMaximum = useParameterByTypeScreenReducer(
		ParameterTypeEnum.MaximumSearchOrderReportScheduleDaysInterval,
	);
	const isEdit = !isNullUndefined(dataEdit);
	//#endregion

	//#region Reducers
	const { countriesUser: countryReducer } = useCountryReducer();
	const { insurers: insurerReducer } = useInsurerReducer();
	const { bodyshops: bodyshopReducer } = useBodyshopReducer();
	const { suppliers: supplierReducer } = useSupplierReducer();
	const { reportScheduleStatus: reportScheduleStatusReducer } = useReportScheduleStatusReducer();
	const { schedulerUsers: schedulerUsersReducer } = useSchedulerUsersReducer();
	const formattedReportScheduleStatus = {
		...reportScheduleStatusReducer,
		data: isNullUndefined(reportScheduleStatusReducer.data)
			? null
			: orderDataByValue(
					reportScheduleStatusReducer.data.map((item) => {
						return {
							key: item.key,
							value: getTranslateByFullKey(item.value),
						};
					}),
				),
	};

	//#endregion

	//#region States
	const [hasClear, setHasClear] = useState<boolean>(false);
	const [defaultValueEditVehicle, setDefaultValueEditVehicle] = useState<string>("");
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);
	const optionDefaultBodyshopDrop = getDefaultOptionDrop(bodyshopReducer.data);
	const optionDefaultInsurerDrop = userIsInsurer() ? getDefaultOptionDrop(insurerReducer.data) : null;
	const INITIAL_FORM_STATE = {
		country: null as KeyValue | null,
		insurer: null as KeyValue | null,
		bodyshopBranch: null as KeyValue | null,
		supplier: null as KeyValue | null,
		startDate: null as Date | null,
		finalDate: null as Date | null,
		isRecurrent: false as boolean,
		vehicleManufacturer: "" as string,
		vehicleModel: "" as string,
		vehicle: "" as string,
		subModel: "" as string,
		username: null as KeyValue[] | null,
		status: null as KeyValue | null,
	};
	type ISearchClaimForm = typeof INITIAL_FORM_STATE;

	const methods = useFormQueryParams<ISearchClaimForm>({
		onSubmit: onSubmit,
		defaultValues: INITIAL_FORM_STATE,
		mapDefaultValues: {
			bodyshopBranch: bodyshopReducer,
			insurer: insurerReducer,
			country: countryReducer,
			status: formattedReportScheduleStatus,
			supplier: supplierReducer,
		},
	});
	const { control, customHandleSubmit, setValue, getValues, clearErrors, reset, trigger, resetField, watch } = methods;
	const watcherCountry = watch("country");
	const watcherBodyshop = watch("bodyshopBranch");
	const watcherInsurer = watch("insurer");
	const watcherSupplier = watch("supplier");
	const watcherIsRecurrent = watch("isRecurrent");
	//#endregion

	//#region Core functions
	function initialLoadForm() {
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);

		reset({
			...INITIAL_FORM_STATE,
			startDate: firstDateResult,
			finalDate: secondDateResult,
		});
	}

	function handleSchedule() {
		trigger();
		const formValues = getValues();
		const isEmptyUsername = isNullUndefined(formValues.username) || formValues.username.length === 0;
		const ignoreValidationsDate: TypeKeysValidationsDate[] = formValues.isRecurrent
			? ["isInvalidFinalDate", "finalDateGreaterThanCurrentDate"]
			: [];
		validateFormDate(formValues.startDate, formValues.finalDate, parameterRangeDaysMaximum, ignoreValidationsDate);

		if (isEmptyUsername) {
			toast.error(getTranslateByKey("information.usernameRequired", true));
			methods.setError("username", {
				message: getTranslateByKey("information.usernameRequired", true),
			});
			return;
		}

		const keysSelectedUsername = isNullUndefined(formValues.username)
			? []
			: formValues.username.map((i) => String(i.key));
		const request: ProcurementReportScheduleSaveRequest = {
			...(isEdit && {
				guidProcurementReportSchedule: dataEdit.guidProcurementReportSchedule,
			}),
			guidCountry: isNullUndefined(formValues.country) ? "" : String(formValues.country.key),
			guidBodyShop: isNullUndefined(formValues.bodyshopBranch) ? "" : String(formValues.bodyshopBranch.key),
			guidInsurer: isNullUndefined(formValues.insurer) ? "" : String(formValues.insurer.key),
			guidSupplier: isNullUndefined(formValues.supplier) ? "" : String(formValues.supplier.key),
			guidSubModel: formValues.subModel,
			guidVehicleModel: formValues.vehicleModel,
			guidVehicleManufacturer: formValues.vehicleManufacturer,
			listUsers: keysSelectedUsername,
			recurrent: formValues.isRecurrent,
			finalDate: isNullUndefined(formValues.finalDate) ? "" : formatDateToISOString(formValues.finalDate),
			startDate: isNullUndefined(formValues.startDate) ? "" : formatDateToISOString(formValues.startDate),
		};

		setIsLoadingSave(true);
		procurementReportScheduleApi
			.saveProcurementReportSchedule(request)
			.then(() => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				updateDataEdit(null);
				handleClear();
				customHandleSubmit();
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}
	function handleBack() {
		navigate(RoutesConstants.reportOrder);
	}
	function handleClear() {
		onResetSearch();
		reset(INITIAL_FORM_STATE);
		updateDataEdit(null);

		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		setValue("startDate", firstDateResult);
		setValue("finalDate", secondDateResult);
		clearErrors();
		setHasClear(true);

		if (optionDefaultCountryDrop) setValue("country", optionDefaultCountryDrop);
		if (optionDefaultCountryDrop !== null) {
			setValue("country", optionDefaultCountryDrop);
			callBodyshopApiDomain();
			callInsurerApiDomain();
			callSupplierApiDomain(optionDefaultCountryDrop);
		} else {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		}
	}
	function loadFormEdit() {
		if (!isNullUndefined(dataEdit)) {
			const isRecurrent = dataEdit.idProcurementReportScheduleStatus === StatusProcurementReportScheduleEnum.Recurrent;
			reset();
			setDefaultValueEditVehicle("");

			setValue("country", watcherCountry);
			callSupplierApiDomain(watcherCountry);

			if (!isNullUndefined(dataEdit.guidInsurer)) {
				const formattedInsurer = {
					key: dataEdit.guidInsurer,
					value: dataEdit.insurerName,
				};
				setValue("insurer", formattedInsurer);
			}

			if (!isNullUndefined(dataEdit.guidBodyShop)) {
				const formattedBodyshop = {
					key: dataEdit.guidBodyShop,
					value: dataEdit.bodyShopName,
				};
				setValue("bodyshopBranch", formattedBodyshop);
			}

			if (!isNullUndefined(dataEdit.guidSupplier)) {
				setValue("supplier", {
					key: dataEdit.guidSupplier,
					value: dataEdit.supplierName,
				});
			}
			if (!isNullUndefined(dataEdit.startDate)) {
				setValue("startDate", new Date(dataEdit.startDate));
			}
			if (!isNullUndefined(dataEdit.finalDate) && !isRecurrent) {
				setValue("finalDate", new Date(dataEdit.finalDate));
			}
			if (isRecurrent) {
				setValue("isRecurrent", isRecurrent);
				setValue("finalDate", null);
			}
			if (!isNullUndefined(dataEdit.guidVehicleManufacturer)) {
				setValue("vehicleManufacturer", dataEdit.guidVehicleManufacturer);
			}
			if (!isNullUndefined(dataEdit.guidVehicleModel)) {
				setValue("vehicleModel", dataEdit.guidVehicleModel);
			}
			if (!isNullUndefined(dataEdit.guidSubModel)) {
				setValue("subModel", dataEdit.guidSubModel);
			}
			if (
				!isNullUndefined(dataEdit.vehicleFullName) &&
				dataEdit.vehicleFullName !== getTranslateByKey("information.all")
			) {
				setValue("vehicle", dataEdit.vehicleFullName);
				setDefaultValueEditVehicle(dataEdit.vehicleFullName);
			}

			if (!isNullUndefined(dataEdit.listUsers)) {
				setValue("username", dataEdit.listUsers);
			}
			if (!isNullUndefined(dataEdit.idProcurementReportScheduleStatus)) {
				const findStatus = formattedReportScheduleStatus.data?.find(
					(item) => item.key === String(dataEdit.idProcurementReportScheduleStatus),
				);
				setValue("status", findStatus ?? null);
			}
		}
	}

	function onSubmit(formInputs: ISearchClaimForm) {
		updateDataEdit(null);
		validateFormDate(formInputs.startDate, formInputs.finalDate, parameterRangeDaysMaximum);

		const keysSelectedUsername = isNullUndefined(formInputs.username)
			? []
			: formInputs.username.map((i) => String(i.key));
		const request: ProcurementReportScheduleSearchGridRequest = {
			guidCountry: String(formInputs.country?.key),
			listUsers: keysSelectedUsername,
			recurrent: formInputs.isRecurrent,
			finalDate: isNullUndefined(formInputs.finalDate) ? "" : formatDateToISOString(formInputs.finalDate),
			startDate: isNullUndefined(formInputs.startDate) ? "" : formatDateToISOString(formInputs.startDate),
			guidBodyShop: String(formInputs.bodyshopBranch?.key),
			guidInsurer: String(formInputs.insurer?.key),
			guidSupplier: String(formInputs.supplier?.key),
			guidSubModel: formInputs.subModel,
			guidVehicleModel: formInputs.vehicleModel,
			guidProcurementReportSchedule: "",
			guidVehicleManufacturer: formInputs.vehicleManufacturer,
			idProcurementReportScheduleStatus: isNullUndefined(formInputs.status) ? null : Number(formInputs.status.key),
		};

		getData(removeEmptyValues(request));
		setShowFilters(false);
	}

	function existsInDrop(data: KeyValue[], nameField: any) {
		const fieldKeySelected = getValues(nameField)?.key;
		const existsInData = data.find((item) => item.key === fieldKeySelected);

		if (!existsInData) {
			setValue(nameField, null);
		}
	}

	function handleToggleRecurrent(isRecurrent: boolean) {
		if (isRecurrent) {
			setValue("finalDate", null);
			return;
		}
		setValue("finalDate", initalDateForm());
	}
	// #endregion

	// #region Async functions
	function callInsurerApiDomain(bodyshopSelected: KeyValue | null = null, clearFields: boolean = true) {
		// Clean fields & reducers
		if (clearFields) {
			dispatch(resetReducerInsurers());
		}

		if (optionDefaultCountryDrop !== null) {
			const request: InsurerFilterRequest = {
				...(bodyshopSelected && { guidBodyShop: String(bodyshopSelected.key) }),
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getInsurers(request));
		}
	}

	async function callSupplierApiDomain(countrySelected: KeyValue | null) {
		resetField("supplier");
		dispatch(resetReducerSuppliers());

		if (!isNullUndefined(countrySelected)) {
			const request: SupplierFilterRequest = {
				guidCountry: String(countrySelected.key),
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getSuppliers(request));
		}
	}

	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null, clearFields: boolean = true) {
		// Clean fields & reducers
		if (clearFields) {
			dispatch(resetReducerBodyshops());
		}

		if (optionDefaultCountryDrop !== null) {
			const request: BodyShopFilterRequest = {
				...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getBodyshops(request));
		}
	}

	function existsInDropUsername(data: KeyValue[] | null) {
		const optionsSelected = getValues("username");

		if (isNullUndefined(optionsSelected) || isNullUndefined(data)) return;
		const optionsFiltered = optionsSelected.filter((optionSelected) =>
			data.find((item) => item.key === optionSelected.key),
		);
		setValue("username", optionsFiltered);
	}

	//#endregion

	//#region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		if (isNullUndefined(formattedReportScheduleStatus.data)) dispatch(getReportScheduleStatus());
		initialLoadForm();
		return () => {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerReportScheduleStatus());
		};
	}, []);

	useEffect(() => {
		setShowFilters(true);
		loadFormEdit();
	}, [JSON.stringify(dataEdit)]);

	useEffect(() => {
		if (!isNullUndefined(optionDefaultCountryDrop)) {
			setValue("country", optionDefaultCountryDrop);
			callBodyshopApiDomain();
			callInsurerApiDomain();
			callSupplierApiDomain(optionDefaultCountryDrop);
		}
	}, [optionDefaultCountryDrop]);

	useEffect(() => {
		if (insurerReducer.data && !insurerReducer.isLoading) {
			const isTypeCompanyUserInsurer = userIsInsurer();
			existsInDrop(insurerReducer.data, "insurer");
			if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop) callBodyshopApiDomain(optionDefaultInsurerDrop);
		}
	}, [JSON.stringify(insurerReducer.data), insurerReducer.isLoading]);

	useEffect(() => {
		if (bodyshopReducer.data && !bodyshopReducer.isLoading) {
			const isTypeCompanyUserBodyshop = userIsBodyshop();
			existsInDrop(bodyshopReducer.data, "bodyshopBranch");
			if (isTypeCompanyUserBodyshop && optionDefaultBodyshopDrop) callInsurerApiDomain(optionDefaultBodyshopDrop);
		}
	}, [JSON.stringify(bodyshopReducer.data), bodyshopReducer.isLoading]);
	useEffect(() => {
		dispatch(
			getSchedulerUsers({
				guidCountry: isNullUndefined(watcherCountry?.key) ? Guid.EMPTY : String(watcherCountry.key),
				guidBodyShop: isNullUndefined(watcherBodyshop?.key) ? Guid.EMPTY : String(watcherBodyshop.key),
				guidInsurer: isNullUndefined(watcherInsurer?.key) ? Guid.EMPTY : String(watcherInsurer.key),
				guidSupplier: isNullUndefined(watcherSupplier?.key) ? Guid.EMPTY : String(watcherSupplier.key),
			}),
		);
	}, [watcherCountry?.key, watcherBodyshop?.key, watcherInsurer?.key, watcherSupplier?.key]);

	useEffect(() => {
		existsInDropUsername(schedulerUsersReducer.data);
	}, [JSON.stringify(schedulerUsersReducer.data)]);
	//#endregion

	//#region Render constants
	const isDisabledInsurersDrop = () => userIsInsurer() && insurerReducer.data?.length === 1;
	const isDisabledBodyshopDrop = () => userIsBodyshop() && bodyshopReducer?.data?.length === 1;

	//#endregion

	const disabledCountry =
		countryReducer.isLoading ||
		isNullUndefined(countryReducer.data) ||
		countryReducer.data.length === 1 ||
		countryReducer.data.length === 0;
	const isDefaultValueUniqueBodyshop = userIsBodyshop();
	const isDefaultValueUniqueInsurer = userIsInsurer() || optionDefaultInsurerDrop !== null;

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<FormProvider {...(methods as any)}>
				<form onSubmit={customHandleSubmit} noValidate>
					<S.Container>
						<KogAutoCompleteControlled
							nameCustom="country"
							id="id-scheduleOrderReport-select-country"
							isDefaultValueUnique
							setValueCustom={setValue}
							label={getTranslateByKey("information.country")}
							control={control}
							rulesCustom={{ required: true }}
							options={countryReducer.data}
							isLoading={countryReducer.isLoading}
							disabled={disabledCountry}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								if (optionSelected === null) {
									if (optionDefaultBodyshopDrop != null) {
										callInsurerApiDomain(optionDefaultBodyshopDrop);
									} else {
										callInsurerApiDomain();
									}
								}
								callBodyshopApiDomain(optionSelected);
							}}
						/>
						<KogAutoCompleteControlled
							nameCustom="insurer"
							id="id-order-report-schedule-select-insurer"
							isDefaultValueUnique={isDefaultValueUniqueInsurer}
							setValueCustom={setValue}
							label={getTranslateByKey("information.insurer")}
							control={control}
							rulesCustom={{ required: false }}
							errorMessageCustom=""
							hasErrorCustom={false}
							options={insurerReducer.data}
							isLoading={insurerReducer.isLoading}
							disabled={isDisabledInsurersDrop()}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								if (optionSelected === null) {
									if (optionDefaultBodyshopDrop != null) {
										callInsurerApiDomain(optionDefaultBodyshopDrop);
									} else {
										callInsurerApiDomain();
									}
								}
								callBodyshopApiDomain(optionSelected);
							}}
						/>
						<KogAutoCompleteControlled
							nameCustom="bodyshopBranch"
							id="id-order-report-schedule-select-bodyshopBranch"
							isDefaultValueUnique={isDefaultValueUniqueBodyshop}
							setValueCustom={setValue}
							label={getTranslateByKey("information.bodyshopBranch")}
							control={control}
							rulesCustom={{ required: false }}
							options={bodyshopReducer.data}
							isLoading={bodyshopReducer.isLoading}
							disabled={isDisabledBodyshopDrop()}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								const isTypeCompanyUserInsurer = userIsInsurer();
								if (optionSelected === null) {
									if (optionDefaultInsurerDrop != null) {
										callBodyshopApiDomain(optionDefaultInsurerDrop);
									} else {
										callBodyshopApiDomain();
									}
								}
								if (!isTypeCompanyUserInsurer) callInsurerApiDomain(optionSelected);
							}}
						/>
						<KogAutoCompleteControlled
							nameCustom="supplier"
							id="id-order-report-schedule-select-supplier"
							isDefaultValueUnique
							setValueCustom={setValue}
							label={getTranslateByKey("information.supplier")}
							control={control}
							rulesCustom={{ required: false }}
							options={supplierReducer.data}
							isLoading={supplierReducer.isLoading}
							disabled={isNullUndefined(supplierReducer.data) || userIsSupplier()}
							onChangeCustom={() => {}}
						/>
						<KogKeyboardDatePickerContainer
							idScreen="id-order-report-schedule"
							nameStartDate="startDate"
							nameFinalDate="finalDate"
							nameCheckbox="isRecurrent"
							labelCheckbox={`${getTranslateByKey("information.recurrent")}?`}
							parameterRangeDays={parameterRangeDaysMaximum}
							control={control}
							disabledFields={watcherIsRecurrent ? ["finalDate"] : []}
							ignoreKeyValidations={watcherIsRecurrent ? getAllKeysIgnoreValidationsDate() : []}
							onChangeCheckbox={handleToggleRecurrent}
						/>
						<VehicleAutoCompleteComponent
							hasClearValue={hasClear}
							setHasClearValue={setHasClear}
							handleChangeVehicle={(vehicle) => {
								setValue("vehicleManufacturer", vehicle?.guidVehicleManufacturer);
								setValue("vehicleModel", vehicle?.guidVehicleModel || "");
								setValue("subModel", vehicle?.guidSubModel || "");
								setValue("vehicle", vehicle?.value);
							}}
							vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
							vehicleValueProps={defaultValueEditVehicle}
						/>

						<KogInputAutoCompleteCheckControlled
							name="username"
							control={control}
							options={schedulerUsersReducer.data ?? []}
							label={getTranslateByKey("information.username")}
							isLoading={schedulerUsersReducer.isLoading}
							controllerProps={{ rules: { required: false } }}
							customInputProps={{
								required: true,
								id: "id-order-report-schedule-multiSelect-username",
							}}
							selectAllLabel={getTranslateByKey("information.selectAll")}
							setValueCustom={setValue}
						/>
						<KogAutoCompleteControlled
							nameCustom="status"
							id="id-order-report-schedule-select-status"
							isDefaultValueUnique
							setValueCustom={setValue}
							label={getTranslateByKey("information.status")}
							control={control}
							rulesCustom={{ required: false }}
							options={formattedReportScheduleStatus.data}
							isLoading={formattedReportScheduleStatus.isLoading}
							onChangeCustom={() => {}}
						/>
						<S.AreaButtons>
							<SecondaryButton id="id-order-report-schedule-btn-back" onClick={handleBack} type="button">
								{getTranslateByKey("information.back")}
							</SecondaryButton>
							<SecondaryButton id="id-order-report-schedule-btn-clear" type="button" onClick={handleClear}>
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
							<SecondaryButton id="id-order-report-schedule-btn-search" type="submit">
								{getTranslateByKey("information.search")}
							</SecondaryButton>
							<Button
								id="id-order-report-schedule-btn-submit"
								type="button"
								isLoading={isLoadingSave}
								onClick={methods.handleSubmit(handleSchedule)}
							>
								{isEdit ? getTranslateByKey("information.edit") : getTranslateByKey("information.schedule")}
							</Button>
						</S.AreaButtons>
					</S.Container>
				</form>
			</FormProvider>
		</Box>
	);
}

function FiltersOrderReportScheduleWrapper(
	props: Omit<IFiltersOrderReportScheduleProps, "showFilters" | "setShowFilters">,
) {
	//#region States
	const [showFilters, setShowFilters] = useState<boolean>(true);
	//#endregion

	//#region HandleFunctions
	function handleFilters() {
		setShowFilters((prev) => !prev);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}
	//#endregion

	const mergedProps: IFiltersOrderReportScheduleProps = {
		...props,
		showFilters,
		setShowFilters,
	};
	return (
		<>
			<AreaButton>
				<Button onClick={handleFilters} id="id-order-report-schedule-btn-filters">
					{getTranslateByKey("information.filters")}
				</Button>
			</AreaButton>
			<FiltersOrderReportSchedule {...mergedProps} />
		</>
	);
}
export default FiltersOrderReportScheduleWrapper;
